import { useMutation, useQuery } from "@tanstack/react-query";
import { FacilityType } from "../config/enums/FacilityType.enum";
import { IngredientDataTypesOption } from "../config/enums/IngredientType.enum";
import { successMessage } from "../helpers/ErrorHandler";
import { ApiServiceErr, MutOptions, axiosApi } from "./apiService";

export const useGetFacilityWholeList = (facilityType: any) =>
  useQuery(
    ["getFacilityWholeList", facilityType],
    async () => {
      const response = await axiosApi.get(
        `/facility/list?facilityType=${FacilityType.FACTORY_AND_STORAGE}&facilityType=${facilityType}`
      );

      const newData = response?.data?.map((value: any, index: any) => {
        return {
          key: `facility_${index}`,
          text: value?.facilityName,
          value: value?._id,
        };
      });
      return newData;
    },
    {
      refetchOnWindowFocus: false,
    }
  );

export const useGetAllFacilitiesData = (facilityType: any) =>
  useQuery(
    ["getAllFacilitiesRaw", facilityType],
    async () => {
      const response = await axiosApi.get(
        `/facility/list/all?facilityType=${FacilityType.FACTORY_AND_STORAGE}&facilityType=${facilityType}`
      );
      return response.data; 
    },
    { refetchOnWindowFocus: false }
  );

export const useGetAllFacilities = (facilityType: any) => {
  const { data, ...rest } = useGetAllFacilitiesData(facilityType);

  const newData = data?.map((value: any, index: any) => ({
    key: `facility_${index}`,
    text: value?.facilityName,
    value: value?._id,
  }));

  return { data: newData, ...rest };
};

export const useGetAllFacilitiesForSearch = (facilityType: any) => {
  const { data, isLoading, ...rest } = useGetAllFacilitiesData(facilityType);

  const availableFacilities = data?.map((f: any) => ({
    id: f._id,
    title: f.facilityName,
    facilityData: f,
  })) ?? [];

  return { availableFacilities, isLoading, ...rest };
};

export const useCalculateData = (opt?: MutOptions<string>) =>
  useMutation<string, ApiServiceErr, any>(async (id) => {
    const response = await axiosApi.put(`/farm-gate/queue/${id}`);
    successMessage(response.data);
    return response.data;
  }, opt);

export const usePostFarmGateInternalTransportData = (data?: any) => {
  return useMutation<string, ApiServiceErr, any>(async (data) => {
    const response = await axiosApi.post(
      `/farm-gate/internal-transport?farmGateId=${data?.farmGateId}`,
      data?.transportData
    );
    return response.data;
  });
};

export const usePutFarmGateInternalTransportData = (data?: any) => {
  return useMutation<string, ApiServiceErr, any>(async (data) => {
    const response = await axiosApi.put(
      `/farm-gate/internal-transport/${data?.internalTransportId}?farmGateId=${data?.farmGateId}`,
      data?.transportData
    );
    return response.data;
  });
};

export const useDeleteFarmGateInternalTransportData = (data?: any) => {
  return useMutation<string, ApiServiceErr, any>(async (data) => {
    const response = await axiosApi.delete(
      `/farm-gate/internal-transport/${data?.internalTransportId}?farmGateId=${data?.farmGateId}`
    );
    return response.data;
  });
};

export const useGetFarmGateInternalTransportById = (
  farmGateInternalTransportId: any
) =>
  useQuery(
    ["getFarmGateInternalTransportById"],
    async () => {
      const response = await axiosApi.get(
        `/farm-gate/internal-transport/${farmGateInternalTransportId}`
      );
      return response?.data;
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!farmGateInternalTransportId,
    }
  );

export const usePostSimulationFarmGateInternalTransportData = (data?: any) => {
  return useMutation<string, ApiServiceErr, any>(async (data) => {
    const response = await axiosApi.post(
      `/farm-gate/internal-transport?farmGateId=${data?.farmGateId}`,
      data?.transportData
    );
    return response.data;
  });
};

export const usePutSimulationFarmGateInternalTransportData = (data?: any) => {
  return useMutation<string, ApiServiceErr, any>(async (data) => {
    const response = await axiosApi.put(
      `/farm-gate/internal-transport/${data?.internalTransportId}?farmGateId=${data?.farmGateId}`,
      data?.transportData
    );
    return response.data;
  });
};

export const useDeleteSimulationFarmGateInternalTransportData = (
  data?: any
) => {
  return useMutation<string, ApiServiceErr, any>(async (data) => {
    const response = await axiosApi.delete(
      `/farm-gate/internal-transport/${data?.internalTransportId}?farmGateId=${data?.farmGateId}`
    );
    return response.data;
  });
};

export const useGetSimulationFarmGateInternalTransportById = (
  farmGateInternalTransportId: any
) =>
  useQuery(
    ["getFarmGateSimulationInternalTransportById"],
    async () => {
      const response = await axiosApi.get(
        `/farm-gate/internal-transport/${farmGateInternalTransportId}`
      );
      return response?.data;
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!farmGateInternalTransportId,
    }
  );

export const useGetTransportTypesList = () =>
  useQuery(
    ["getTransportTypesList"],
    async () => {
      const response = await axiosApi.get(`/static-data/transportation-types`);

      const newD = await response?.data?.map((type: any) => {
        const newIngredient: any = {
          key: type?.id,
          text: type?.transportation_name,
          value: type?.id,
        };
        return newIngredient;
      });
      return newD;
    },
    {
      refetchOnWindowFocus: false,
    }
  );

export const usePostFarmGateInternalStorageData = (data?: any) => {
  return useMutation<string, ApiServiceErr, any>(async (data) => {
    const response = await axiosApi.post(
      `/farm-gate/internal-storage?farmGateId=${data?.farmGateId}`,
      data?.storageData
    );
    return response.data;
  });
};

export const usePutFarmGateInternalStorageData = (data?: any) => {
  return useMutation<string, ApiServiceErr, any>(async (data) => {
    const response = await axiosApi.put(
      `/farm-gate/internal-storage/${data?.internalStorageId}?farmGateId=${data?.farmGateId}`,
      data?.storageData
    );
    return response.data;
  });
};

export const useDeleteFarmGateInternalStorageData = (data?: any) => {
  return useMutation<string, ApiServiceErr, any>(async (data) => {
    const response = await axiosApi.delete(
      `/farm-gate/internal-storage/${data?.internalStorageId}?farmGateId=${data?.farmGateId}`
    );
    return response.data;
  });
};

export const useGetFarmGateInternalStorageById = (
  farmGateInternalStorageId: any
) =>
  useQuery(
    ["getFarmGateInternalStorageById"],
    async () => {
      const response = await axiosApi.get(
        `/farm-gate/internal-storage/${farmGateInternalStorageId}`
      );
      return response?.data;
    },
    {
      refetchOnWindowFocus: false,
      enabled: false,
    }
  );

export const useGetFacilityList = () =>
  useQuery(
    ["getFacilityList"],
    async () => {
      const response = await axiosApi.get(`/facility`);

      const newD = await response?.data?.result?.map((facility: any) => {
        return {
          key: facility?._id,
          text: facility?.facilityName,
          value: facility?._id,
        };
      });
      return newD;
    },
    {
      refetchOnWindowFocus: false,
    }
  );

export const usePostFarmGateProcessData = (data?: any) => {
  return useMutation<string, ApiServiceErr, any>(async (data) => {
    const response = await axiosApi.post(
      `/farm-gate/processing/${data?.farmGateId}`,
      data
    );
    return response.data;
  });
};

export const usePutFarmGateProcessData = (data?: any) => {
  return useMutation<string, ApiServiceErr, any>(async (data) => {
    const response = await axiosApi.put(
      `/farm-gate/processing/${data?.processId}`,
      data
    );
    return response.data;
  });
};

export const useDeleteFarmGateProcessData = (data?: any) => {
  return useMutation<string, ApiServiceErr, any>(async (data) => {
    const response = await axiosApi.delete(
      `/farm-gate/processing/${data?.processId}?farmGateId=${data?.farmGateId}`
    );
    return response.data;
  });
};

export const useGetFarmGateProcessDataById = (farmGateProcessId: any) =>
  useQuery(
    ["getFarmGateProcessDataById", farmGateProcessId],
    async () => {
      const response = await axiosApi.get(
        `/farm-gate/processing/${farmGateProcessId}`
      );
      return response?.data;
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!farmGateProcessId,
    }
  );

export const useGetProcessTypesWholeList = (dataType: any = IngredientDataTypesOption.SPECIFIC) =>
  useQuery(
    ["getProcessTypesWholeList", dataType],
    async () => {
      const status = dataType === IngredientDataTypesOption.GENERIC
      const response = await axiosApi.get(`/static-data/process-types`, {
        params: { isGeneric: status }
      });
      const newData = response?.data?.map((value: any, index: any) => {
        return {
          key: `processingType_${index}`,
          text: value?.process_name,
          value: value?.id,
        };
      });
      return newData;
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!dataType,
    }
  );
